import { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  setDoc,
} from "firebase/firestore";
import { Row, Col, Card, Button, Form, Badge, Alert } from "react-bootstrap";
import {
  FaRupeeSign,
  FaSave,
  FaChartLine,
  FaSyncAlt,
  FaArrowLeft,
  FaBullseye,
  FaShieldAlt,
  FaLock,
} from "react-icons/fa";

import { db } from "../firebase";

function CustomerStockCards({ stockCalls, customer }) {
  const [savedTrades, setSavedTrades] = useState({});
  const [investedAmounts, setInvestedAmounts] = useState({});
  const [quantities, setQuantities] = useState({});
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [flippedCards, setFlippedCards] = useState({});

  const normalizeEmail = (value) => {
    return value?.trim().toLowerCase();
  };

  const createTradeId = (customerEmail, stockCallId) => {
    return `${normalizeEmail(customerEmail)}_${stockCallId}`;
  };

  const toggleFlip = (stockCallId) => {
    setFlippedCards((previous) => ({
      ...previous,
      [stockCallId]: !previous[stockCallId],
    }));
  };

  const sortCallsActiveFirst = (calls) => {
    return [...calls].sort((a, b) => {
      const aIsActive = (a.status || "Active") === "Active";
      const bIsActive = (b.status || "Active") === "Active";

      if (aIsActive && !bIsActive) return -1;
      if (!aIsActive && bIsActive) return 1;

      return (
        Number(b.createdAt || b.updatedAt || 0) -
        Number(a.createdAt || a.updatedAt || 0)
      );
    });
  };

  const loadCustomerTrades = async () => {
    if (!customer?.email) return;

    const cleanEmail = normalizeEmail(customer.email);

    const tradesQuery = query(
      collection(db, "customerTrades"),
      where("customerEmail", "==", cleanEmail)
    );

    const tradesSnapshot = await getDocs(tradesQuery);

    const tradesMap = {};
    const investedMap = {};
    const quantityMap = {};

    tradesSnapshot.docs.forEach((tradeDocument) => {
      const trade = {
        id: tradeDocument.id,
        ...tradeDocument.data(),
      };

      tradesMap[trade.stockCallId] = trade;
      investedMap[trade.stockCallId] = trade.investedAmount || "";
      quantityMap[trade.stockCallId] = trade.quantity || "";
    });

    setSavedTrades(tradesMap);
    setInvestedAmounts(investedMap);
    setQuantities(quantityMap);
  };

  useEffect(() => {
    loadCustomerTrades();
  }, [customer?.email]);

  const saveTrade = async (stockCall) => {
    try {
      if (!customer?.email) {
        alert("Customer email not found. Please login again.");
        return;
      }

      const investedAmount = Number(investedAmounts[stockCall.id] || 0);
      const quantity = Number(quantities[stockCall.id] || 0);

      if (investedAmount <= 0 || quantity <= 0) {
        alert("Please enter valid invested amount and quantity.");
        return;
      }

      const cleanEmail = normalizeEmail(customer.email);
      const tradeId = createTradeId(cleanEmail, stockCall.id);

      const entryPrice = Number(stockCall.entryPrice || 0);
      const currentPrice = Number(stockCall.currentPrice || entryPrice || 0);

      const profitLossAmount = (currentPrice - entryPrice) * quantity;
      const profitLossPercent =
        entryPrice > 0 ? ((currentPrice - entryPrice) / entryPrice) * 100 : 0;

      await setDoc(
        doc(db, "customerTrades", tradeId),
        {
          customerId: customer.id || cleanEmail,
          customerName: customer.name || "",
          customerEmail: cleanEmail,

          stockCallId: stockCall.id,
          stockName: stockCall.stockName || "",
          callType: stockCall.callType || "",
          callStatus: stockCall.status || "Active",

          buyPrice: stockCall.buyPrice || "",
          entryPrice: stockCall.entryPrice || "",
          targetPrice: stockCall.targetPrice || "",
          stopLoss: stockCall.stopLoss || "",
          currentPrice: stockCall.currentPrice || "",

          investedAmount,
          quantity,

          investmentConfirmed:
            savedTrades[stockCall.id]?.investmentConfirmed || false,

          showTargetToCustomer:
            savedTrades[stockCall.id]?.showTargetToCustomer || false,

          showStopLossToCustomer:
            savedTrades[stockCall.id]?.showStopLossToCustomer || false,

          manualProfitLossPercent:
            savedTrades[stockCall.id]?.manualProfitLossPercent || "",
          manualProfitLossAmount:
            savedTrades[stockCall.id]?.manualProfitLossAmount || "",
          isManualProfitLoss:
            savedTrades[stockCall.id]?.isManualProfitLoss || false,

          autoProfitLossAmount: Number(profitLossAmount.toFixed(2)),
          autoProfitLossPercent: Number(profitLossPercent.toFixed(2)),

          recommendationDate: stockCall.recommendationDate || "",
          recommendationTime: stockCall.recommendationTime || "",

          createdAt: savedTrades[stockCall.id]?.createdAt || Date.now(),
          updatedAt: Date.now(),
        },
        { merge: true }
      );

      alert(
        "✅ Investment details saved successfully. Admin will review and confirm it."
      );

      loadCustomerTrades();
    } catch (error) {
      console.error(error);
      alert("Unable to save investment details.");
    }
  };

  const getProfitLoss = (stockCall) => {
    const savedTrade = savedTrades[stockCall.id];

    if (!savedTrade) {
      return {
        amount: 0,
        percent: 0,
      };
    }

    if (savedTrade.isManualProfitLoss) {
      return {
        amount: Number(savedTrade.manualProfitLossAmount || 0),
        percent: Number(savedTrade.manualProfitLossPercent || 0),
      };
    }

    const entryPrice = Number(stockCall.entryPrice || savedTrade.entryPrice || 0);
    const currentPrice = Number(
      stockCall.currentPrice || savedTrade.currentPrice || entryPrice || 0
    );
    const quantity = Number(savedTrade.quantity || 0);

    const amount = (currentPrice - entryPrice) * quantity;
    const percent =
      entryPrice > 0 ? ((currentPrice - entryPrice) / entryPrice) * 100 : 0;

    return {
      amount: Number(amount.toFixed(2)),
      percent: Number(percent.toFixed(2)),
    };
  };

  const sortedStockCalls = sortCallsActiveFirst(stockCalls);

  const filteredCalls =
    selectedFilter === "All"
      ? sortedStockCalls
      : sortedStockCalls.filter((call) => call.callType === selectedFilter);

  return (
    <>
      <style>
        {`
          .tw-flip-card {
            background: transparent;
            width: 100%;
            min-height: 450px;
            perspective: 1000px;
          }

          .tw-flip-inner {
            position: relative;
            width: 100%;
            min-height: 450px;
            transition: transform 0.6s;
            transform-style: preserve-3d;
          }

          .tw-flip-inner.flipped {
            transform: rotateY(180deg);
          }

          .tw-flip-front,
          .tw-flip-back {
            position: absolute;
            width: 100%;
            min-height: 450px;
            backface-visibility: hidden;
            border-radius: 18px;
          }

          .tw-flip-back {
            transform: rotateY(180deg);
          }

          .tw-card-box {
            border-radius: 18px;
            min-height: 450px;
          }
        `}
      </style>

      <Card className="shadow border-0 mb-4" style={{ borderRadius: "18px" }}>
        <Card.Body>
          <Row className="align-items-center">
            <Col md={6}>
              <h4 className="fw-bold mb-1">
                <FaChartLine className="me-2 text-success" />
                Stock Calls
              </h4>

              <p className="text-muted mb-0">
                Active calls are shown first.
              </p>
            </Col>

            <Col md={6} className="text-md-end mt-3 mt-md-0">
              {["All", "Long Term", "Mid Term", "Short Term"].map((filter) => (
                <Button
                  key={filter}
                  size="sm"
                  className="me-2 mb-2"
                  variant={
                    selectedFilter === filter ? "success" : "outline-success"
                  }
                  onClick={() => setSelectedFilter(filter)}
                >
                  {filter}
                </Button>
              ))}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {filteredCalls.length === 0 ? (
        <Alert variant="info">
          No stock calls available for your current access.
        </Alert>
      ) : (
        <Row>
          {filteredCalls.map((stockCall) => {
            const savedTrade = savedTrades[stockCall.id];
            const profitLoss = getProfitLoss(stockCall);
            const isFlipped = flippedCards[stockCall.id];

            const canSeeTarget =
              savedTrade?.investmentConfirmed === true &&
              savedTrade?.showTargetToCustomer === true;

            const canSeeStopLoss =
              savedTrade?.investmentConfirmed === true &&
              savedTrade?.showStopLossToCustomer === true;

            return (
              <Col md={6} lg={4} className="mb-4" key={stockCall.id}>
                <div className="tw-flip-card">
                  <div
                    className={
                      isFlipped ? "tw-flip-inner flipped" : "tw-flip-inner"
                    }
                  >
                    <div className="tw-flip-front">
                      <Card className="shadow border-0 h-100 tw-card-box">
                        <Card.Body className="d-flex flex-column">
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <div>
                              <h4 className="fw-bold mb-2">
                                {stockCall.stockName}
                              </h4>

                              <Badge bg="info" text="dark">
                                {stockCall.callType}
                              </Badge>
                            </div>

                            <Badge
                              bg={
                                (stockCall.status || "Active") === "Active"
                                  ? "success"
                                  : "secondary"
                              }
                            >
                              {stockCall.status || "Active"}
                            </Badge>
                          </div>

                          <div
                            className="p-3 mb-3"
                            style={{
                              background: "#f8fafc",
                              borderRadius: "14px",
                            }}
                          >
                            <p className="mb-2">
                              <FaRupeeSign className="me-2 text-success" />
                              <strong>Buy Price:</strong>{" "}
                              {stockCall.buyPrice || "-"}
                            </p>

                            <p className="mb-2">
                              <FaChartLine className="me-2 text-success" />
                              <strong>Current Price:</strong>{" "}
                              {stockCall.currentPrice || "-"}
                            </p>

                            {canSeeTarget && (
                              <p className="mb-2">
                                <FaBullseye className="me-2 text-primary" />
                                <strong>Target:</strong>{" "}
                                {stockCall.targetPrice ||
                                  savedTrade.targetPrice ||
                                  "-"}
                              </p>
                            )}

                            {canSeeStopLoss && (
                              <p className="mb-0">
                                <FaShieldAlt className="me-2 text-danger" />
                                <strong>Stop Loss:</strong>{" "}
                                {stockCall.stopLoss ||
                                  savedTrade.stopLoss ||
                                  "-"}
                              </p>
                            )}

                            {savedTrade && !savedTrade.investmentConfirmed && (
                              <Alert
                                variant="warning"
                                className="py-2 mt-3 mb-0"
                              >
                                <FaLock className="me-2" />
                                Waiting for admin confirmation.
                              </Alert>
                            )}
                          </div>

                          {savedTrade && (
                            <Alert
                              variant={
                                profitLoss.amount >= 0 ? "success" : "danger"
                              }
                              className="py-2"
                            >
                              <strong>P/L:</strong> ₹{profitLoss.amount}{" "}
                              ({profitLoss.percent}%)
                            </Alert>
                          )}

                          <div className="mt-auto">
                            <Button
                              variant="success"
                              className="w-100"
                              onClick={() => toggleFlip(stockCall.id)}
                            >
                              <FaSyncAlt className="me-2" />
                              Update Investment
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </div>

                    <div className="tw-flip-back">
                      <Card className="shadow border-0 h-100 tw-card-box">
                        <Card.Body className="d-flex flex-column">
                          <div className="mb-3">
                            <h5 className="fw-bold mb-1">
                              {stockCall.stockName}
                            </h5>

                            <Badge bg="info" text="dark">
                              {stockCall.callType}
                            </Badge>
                          </div>

                          <Form.Group className="mb-3">
                            <Form.Label>Invested Amount</Form.Label>

                            <Form.Control
                              type="number"
                              placeholder="Enter invested amount"
                              value={investedAmounts[stockCall.id] || ""}
                              onChange={(event) =>
                                setInvestedAmounts({
                                  ...investedAmounts,
                                  [stockCall.id]: event.target.value,
                                })
                              }
                            />
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>Quantity</Form.Label>

                            <Form.Control
                              type="number"
                              placeholder="Enter quantity"
                              value={quantities[stockCall.id] || ""}
                              onChange={(event) =>
                                setQuantities({
                                  ...quantities,
                                  [stockCall.id]: event.target.value,
                                })
                              }
                            />
                          </Form.Group>

                          {savedTrade?.investmentConfirmed === true ? (
                            <Alert variant="success" className="py-2">
                              Investment confirmed by admin.
                            </Alert>
                          ) : savedTrade ? (
                            <Alert variant="warning" className="py-2">
                              Waiting for admin confirmation.
                            </Alert>
                          ) : null}

                          {savedTrade && (
                            <Alert
                              variant={
                                profitLoss.amount >= 0 ? "success" : "danger"
                              }
                              className="py-2"
                            >
                              <strong>P/L:</strong> ₹{profitLoss.amount}{" "}
                              ({profitLoss.percent}%)
                            </Alert>
                          )}

                          <div className="mt-auto">
                            <Button
                              variant="success"
                              className="w-100 mb-2"
                              onClick={() => saveTrade(stockCall)}
                            >
                              <FaSave className="me-2" />
                              {savedTrade
                                ? "Update Investment"
                                : "Save Investment"}
                            </Button>

                            <Button
                              variant="outline-dark"
                              className="w-100"
                              onClick={() => toggleFlip(stockCall.id)}
                            >
                              <FaArrowLeft className="me-2" />
                              Back
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </div>
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>
      )}
    </>
  );
}

export default CustomerStockCards;